import React, { useRef, useState } from "react";
import "./QuoteForm.css";
import quoteHeroVideo from "../assets/videos/1200-143842692_medium.mp4";

const TOTAL_STEPS = 4;

export default function QuoteForm() {
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const [projectType, setProjectType] = useState("");
  const [location, setLocation] = useState("");
  const [area, setArea] = useState("");
  const [stage, setStage] = useState("");
  const [needs, setNeeds] = useState([]);
  const [budget, setBudget] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const submissionId = useRef(
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`
  );

  const goNext = () => {
    setSubmitError("");
    if (step < TOTAL_STEPS) setStep(step + 1);
  };
  const goBack = () => {
    setSubmitError("");
    if (step > 1) setStep(step - 1);
  };

  const toggleNeed = (value) => {
    setNeeds((prev) =>
      prev.includes(value) ? prev.filter((n) => n !== value) : [...prev, value]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting || submitted) return;

    setSubmitError("");
    setIsSubmitting(true);

    try {
     const response = await fetch("https://api.dwelloradesigns.com/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Submission-Id": submissionId.current,
        },
        body: JSON.stringify({
          projectType,
          location,
          area,
          stage,
          needs,
          budget,
          name,
          phone,
          email,
        }),
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok || !result.success) {
        throw new Error(
          result.message || "Unable to submit enquiry. Please try again."
        );
      }


      setSubmitted(true);
    } catch (error) {
      setSubmitError(
        error.message || "Unable to submit enquiry. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
    <section className="quote-hero" aria-labelledby="quote-hero-title">
      <video
        className="quote-hero-video"
        src={quoteHeroVideo}
        autoPlay
        muted
        loop
        playsInline
        aria-hidden="true"
      />
      <div className="quote-hero-overlay" aria-hidden="true"></div>
      <div className="quote-hero-content">
        <p className="quote-hero-label">Start your project</p>
        <h1 id="quote-hero-title">Let&apos;s design your space with intention.</h1>
        <p>
          Share your vision, site details and priorities. Our designers will help
          shape the right path from first conversation to final handover.
        </p>
      </div>
    </section>

    <section className="section-pad quote-shell" id="quote">
      <div className="blob-bg" aria-hidden="true"></div>

      <div className="container quote-inner">
        <div className="quote-copy">
          <p className="eyebrow on-dark">Start Your Project</p>
          <h2>Let's design it right, then build it with the right hands.</h2>
          <p>
            Tell us a little about your space. A Dwellora designer will get
            back to you — usually within a working day.
          </p>
          <div className="quote-contact">
            <span>Pune, Maharashtra</span>
            <a href="tel:+919158914222">+91 91589 14222</a>
            <a href="mailto:contact@dwellorahomes.com">
              contact@dwellorahomes.com
            </a>
          </div>
        </div>

        <div className="form-card">
          {!submitted && (
            <div className="progress">
              <span className={`pd ${step >= 1 ? "done" : ""}`}></span>
              <span className={`pl ${step > 1 ? "done" : ""}`}></span>
              <span className={`pd ${step >= 2 ? "done" : ""}`}></span>
              <span className={`pl ${step > 2 ? "done" : ""}`}></span>
              <span className={`pd ${step >= 3 ? "done" : ""}`}></span>
              <span className={`pl ${step > 3 ? "done" : ""}`}></span>
              <span className={`pd ${step >= 4 ? "done" : ""}`}></span>
            </div>
          )}

          {!submitted && (
            <form onSubmit={handleSubmit} aria-busy={isSubmitting}>
              {step === 1 && (
                <div className="step active">
                  <p className="step-label">Step 01</p>
                  <h3>Tell us about your project</h3>

                  <div className="field-group">
                    <label>Project Type</label>
                    <div className="chip-row">
                      {["Apartment", "Villa", "Independent House", "Office", "Other"].map(
                        (option) => (
                          <button
                            type="button"
                            key={option}
                            className={`chip ${projectType === option ? "sel" : ""}`}
                            onClick={() => setProjectType(option)}
                          >
                            {option}
                          </button>
                        )
                      )}
                    </div>
                  </div>

                  <div className="two-col">
                    <div className="field-group">
                      <label>Location</label>
                      <input
                        type="text"
                        placeholder="e.g. Baner, Pune"
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                      />
                    </div>
                    <div className="field-group">
                      <label>Approx. Area (sq.ft)</label>
                      <input
                        type="text"
                        placeholder="e.g. 1800"
                        value={area}
                        onChange={(e) => setArea(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="step-nav">
                    <span></span>
                    <button type="button" className="btn btn-solid" onClick={goNext}>
                      Continue
                    </button>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="step active">
                  <p className="step-label">Step 02</p>
                  <h3>Where are you in the journey?</h3>

                  <div className="field-group">
                    <label>Project Stage</label>
                    <div className="chip-row">
                      {["Planning", "Under Construction", "Ready Possession", "Renovation"].map(
                        (option) => (
                          <button
                            type="button"
                            key={option}
                            className={`chip ${stage === option ? "sel" : ""}`}
                            onClick={() => setStage(option)}
                          >
                            {option}
                          </button>
                        )
                      )}
                    </div>
                  </div>

                  <div className="field-group">
                    <label>What do you need? (select all that apply)</label>
                    <div className="chip-row">
                      {[
                        "Complete Interior",
                        "Modular Kitchen",
                        "Furniture",
                        "Living Room",
                        "Bedroom",
                        "Custom Carpentry",
                        "Renovation",
                      ].map((option) => (
                        <button
                          type="button"
                          key={option}
                          className={`chip ${needs.includes(option) ? "sel" : ""}`}
                          onClick={() => toggleNeed(option)}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="step-nav">
                    <button type="button" className="back-btn" onClick={goBack}>
                      ← Back
                    </button>
                    <button type="button" className="btn btn-solid" onClick={goNext}>
                      Continue
                    </button>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="step active">
                  <p className="step-label">Step 03</p>
                  <h3>Approximate budget</h3>

                  <div className="field-group">
                    <div className="chip-row">
                      {["Under ₹10L", "₹10L – ₹25L", "₹25L – ₹50L", "₹50L+"].map(
                        (option) => (
                          <button
                            type="button"
                            key={option}
                            className={`chip ${budget === option ? "sel" : ""}`}
                            onClick={() => setBudget(option)}
                          >
                            {option}
                          </button>
                        )
                      )}
                    </div>
                  </div>

                  <div className="step-nav">
                    <button type="button" className="back-btn" onClick={goBack}>
                      ← Back
                    </button>
                    <button type="button" className="btn btn-solid" onClick={goNext}>
                      Continue
                    </button>
                  </div>
                </div>
              )}

              {step === 4 && (
                <div className="step active">
                  <p className="step-label">Step 04</p>
                  <h3>How should we reach you?</h3>

                  <div className="two-col">
                    <div className="field-group">
                      <label>Name</label>
                      <input
                        type="text"
                        placeholder="Your full name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                      />
                    </div>
                    <div className="field-group">
                      <label>Phone</label>
                      <input
                        type="tel"
                        placeholder="+91"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="field-group">
                    <label>Email</label>
                    <input
                      type="email"
                      placeholder="you@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>

                  <div className="step-nav">
                    <button type="button" className="back-btn" onClick={goBack}>
                      ← Back
                    </button>
                    <button
                      type="submit"
                      className="btn btn-electric"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Sending..." : "Request My Free Consultation"}
                    </button>
                  </div>
                </div>
              )}
            </form>
          )}

          {submitError && (
            <p className="submit-error" role="alert">
              {submitError}
            </p>
          )}

          {submitted && (
            <div className="thankyou active">
              <h3>Thank you.</h3>
              <p>A Dwellora Living designer will get in touch with you shortly.</p>
            </div>
          )}
        </div>
      </div>
    </section>
    </>
  );
}
